import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, Calendar, Clock, User, X, BookOpen,
  FileText, AlertCircle, Check
} from 'lucide-react';
import {
  db, collection, query, where, onSnapshot, setDoc, doc, Timestamp
} from '../../firebase';
import styles from '../CSS/WeekCalendar.module.css';

// Importamos el nuevo modal
import NewReservationModal from './NewReservationModal';
import ReservationDetailsModal from './ReservationDetailsModal';

const WeekCalendar = ({ user, space, onCreateReservation, canDelegateReservations = false }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);


  const handleReservationClick = (e, reservation) => {
    e.stopPropagation(); // Evita que se propague al div padre
    setSelectedReservation(reservation);
    setShowDetailsModal(true);
  };
  // Estados para manejar la reserva y el modal
  const [showModal, setShowModal] = useState(false);
  const [selectedRange, setSelectedRange] = useState({
    dayIndex: null,
    startTime: '',
    endTime: '',
    title: '',
    description: ''
  });
  const [existingReservations, setExistingReservations] = useState([]);
  const [hoveredReservation, setHoveredReservation] = useState(null);

  // Feedback para errores/éxitos en la creación de la reserva
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tableRef = useRef(null);

  const weekDays = useMemo(() => generateWeekDays(currentWeekStart), [currentWeekStart]);
  const timeSlots = useMemo(() => generateTimeSlots('08:00', '20:00', 10), []);
  const calendarTitle = getWeekRangeTitle(weekDays);

  // Escucha en tiempo real las reservas de la semana actual en Firestore
  useEffect(() => {
    if (!space) return;
    const startDate = formatYMD(weekDays[0].date);
    const endDate = formatYMD(weekDays[6].date);
    const spaceName = typeof space === 'string' ? space.trim() : space?.name.trim();
    const q = query(
      collection(db, 'reservations'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('space', '==', spaceName)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const reservations = [];
        querySnapshot.forEach((doc) => {
          reservations.push({ id: doc.id, ...doc.data() });
        });
        setExistingReservations(reservations);
      },
      (error) => {
        console.error('Error on reservations snapshot:', error);
      }
    );

    return () => unsubscribe();
  }, [currentWeekStart, space, weekDays]);

  const goNextWeek = () => {
    const next = new Date(currentWeekStart);
    next.setDate(next.getDate() + 7);
    setCurrentWeekStart(next);
  };

  const goPrevWeek = () => {
    const prev = new Date(currentWeekStart);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeekStart(prev);
  };

  /**
   * Retorna la reserva que coincida con el slot dado (si la hay).
   * Comparando fecha + rango de [startTime, endTime).
   */
  const getSlotReservation = (dayIndex, timeSlot) => {
    const currentDate = formatYMD(weekDays[dayIndex].date);
    return existingReservations.find((reservation) =>
      reservation.date === currentDate &&
      timeSlot >= reservation.startTime &&
      timeSlot < reservation.endTime
    );
  };

  /**
   * Cuando se hace hover sobre una celda, actualizamos `hoveredReservation`.
   */
  const handleCellHover = (event, dayIndex, timeSlot) => {
    const newReservation = getSlotReservation(dayIndex, timeSlot);

    if (newReservation) {
      if (hoveredReservation?.id === newReservation.id) {
        // Es la misma reserva sobre la que ya teníamos hover: solo actualizamos la posición
        setHoveredReservation((prev) => ({
          ...prev,
          position: { x: event.clientX, y: event.clientY }
        }));
      } else {
        // Es una reserva distinta (o no había ninguna antes)
        setHoveredReservation({
          ...newReservation,
          position: { x: event.clientX, y: event.clientY }
        });
      }
    } else {
      // No hay reserva en este slot => anulamos tooltip
      if (hoveredReservation !== null) {
        setHoveredReservation(null);
      }
    }
  };

  // (Opcional) Para anular el tooltip al salir de la tabla
  const handleTableMouseLeave = () => {
    setHoveredReservation(null);
  };

  // Manejadores para seleccionar intervalo (drag)
  const handleMouseDown = (dayIndex, slotIndex) => {
    const timeSlot = timeSlots[slotIndex];
    // Si ya está reservado, no permitimos seleccionar
    if (getSlotReservation(dayIndex, timeSlot)) return;
    setIsDragging(true);
    setDragStart({ dayIndex, slotIndex });
    setDragEnd({ dayIndex, slotIndex });
  };

  const handleMouseEnter = (dayIndex, slotIndex) => {
    // Si seguimos arrastrando, actualizamos dragEnd
    if (!isDragging) return;
    setDragEnd({ dayIndex, slotIndex });
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd) {
      setIsDragging(false);
      return;
    }
    if (dragStart.dayIndex !== dragEnd.dayIndex) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }
    const chosenDayIndex = dragStart.dayIndex;
    const startSlot = Math.min(dragStart.slotIndex, dragEnd.slotIndex);
    const endSlot = Math.max(dragStart.slotIndex, dragEnd.slotIndex);

    // Verificamos si alguno de los slots está reservado
    for (let i = startSlot; i <= endSlot; i++) {
      if (getSlotReservation(chosenDayIndex, timeSlots[i])) {
        setFeedback('No puedes seleccionar horarios ya reservados');
        setFeedbackType('error');
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        return;
      }
    }

    // Control de longitud mínima
    if (endSlot - startSlot < 1) {
      setFeedback('Selecciona al menos un intervalo de 10 minutos');
      setFeedbackType('error');
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    // Guardamos rango
    setSelectedRange({
      dayIndex: chosenDayIndex,
      startTime: timeSlots[startSlot],
      endTime: timeSlots[endSlot + 1] || timeSlots[endSlot],
      title: '',
      description: ''
    });
    setShowModal(true);
    setFeedback('');
    setFeedbackType('');
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  // URL del backend para envío de emails
  const API_BASE_URL = 'https://truebavirtual.onrender.com';

  // Función para enviar emails de notificación
  const sendReservationEmails = async (reservationData) => {
    try {
      const creatorName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const creatorEmail = user.email;

      console.log('📧 === INICIANDO ENVÍO DE EMAILS ===');
      console.log('👤 Creador:', { creatorName, creatorEmail });

      let assignedName = null;
      let assignedEmail = null;

      // Extraer datos del usuario asignado
      if (selectedRange.delegatedTo) {
        assignedName = selectedRange.delegatedTo.displayName;
        assignedEmail = selectedRange.delegatedTo.email || null;
        console.log('✅ Usuario asignado:', { assignedName, assignedEmail });
      } else {
        console.log('ℹ️ Sin usuario asignado');
      }

      const formatDate = (dateStr) => {
        if (!dateStr) return 'No especificada';
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-ES', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      };

      // 1. SIEMPRE ENVIAR EMAIL AL CREADOR
      const creatorEmailData = {
        creatorEmail: creatorEmail,
        creatorName: creatorName,
        reservationId: reservationData.id || `RES-${Date.now()}`,
        spaceName: typeof space === 'string' ? space : space?.name || 'Espacio',
        title: selectedRange.title.trim(),
        description: selectedRange.description?.trim() || '',
        date: formatDate(reservationData.date),
        startDate: null,
        endDate: null,
        timeStart: selectedRange.startTime,
        timeEnd: selectedRange.endTime,
        isPeriodic: false
      };

      console.log('📤 Enviando email de confirmación al creador...');

      try {
        const creatorResponse = await fetch(`${API_BASE_URL}/api/reservations/notify-created`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(creatorEmailData)
        });

        const creatorResult = await creatorResponse.json();

        if (!creatorResponse.ok) {
          console.error('❌ Error en respuesta del servidor:', creatorResult);
          console.warn('⚠️ No se pudo enviar email al creador');
        } else {
          console.log('✅ Email enviado al creador exitosamente:', creatorResult.message);
        }
      } catch (creatorError) {
        console.error('❌ Error al enviar email al creador:', creatorError);
      }

      // 2. SI HAY ASIGNADO CON EMAIL Y ES DIFERENTE AL CREADOR
      if (assignedEmail && assignedEmail !== creatorEmail) {
        console.log('📤 Enviando email al usuario asignado...');

        const assignedEmailData = {
          assignedEmail: assignedEmail,
          assignedName: assignedName,
          creatorName: creatorName,
          reservationId: reservationData.id || `RES-${Date.now()}`,
          spaceName: typeof space === 'string' ? space : space?.name || 'Espacio',
          title: selectedRange.title.trim(),
          description: selectedRange.description?.trim() || '',
          date: formatDate(reservationData.date),
          startDate: null,
          endDate: null,
          timeStart: selectedRange.startTime,
          timeEnd: selectedRange.endTime,
          isPeriodic: false
        };

        console.log('📋 Datos del email de asignación:', assignedEmailData);

        try {
          const assignedResponse = await fetch(`${API_BASE_URL}/api/reservations/notify-assigned`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(assignedEmailData)
          });

          const assignedResult = await assignedResponse.json();

          if (!assignedResponse.ok) {
            console.error('❌ Error en respuesta del servidor:', assignedResult);
            console.warn('⚠️ No se pudo enviar email al asignado');
          } else {
            console.log('✅ Email enviado al asignado exitosamente:', assignedResult.message);
          }
        } catch (assignedError) {
          console.error('❌ Error al enviar email al asignado:', assignedError);
        }
      } else {
        if (!assignedEmail) {
          console.log('ℹ️ No se enviará email al asignado (sin email proporcionado)');
        } else if (assignedEmail === creatorEmail) {
          console.log('ℹ️ El asignado es el mismo creador, no se envía email duplicado');
        }
      }

      console.log('📧 === ENVÍO DE EMAILS COMPLETADO ===');

    } catch (error) {
      console.error('❌ Error general al enviar emails:', error);
    }
  };

  // Crea la reserva en Firestore (se usará en el modal)
  const handleCreateReservation = async () => {
    if (!user) {
      setFeedback('Debes iniciar sesión para reservar.');
      setFeedbackType('error');
      return;
    }
    if (!space) {
      setFeedback('Espacio no definido.');
      setFeedbackType('error');
      return;
    }
    if (!selectedRange.title?.trim()) {
      setFeedback('Por favor, ingresa un título para la reserva.');
      setFeedbackType('error');
      return;
    }

    setIsSubmitting(true);
    const selectedDateObj = new Date(weekDays[selectedRange.dayIndex].date);
    const finalDateStr = formatYMD(selectedDateObj);

    const reservationData = {
      type: 'manual',
      space: typeof space === 'string' ? space.trim() : space?.name.trim() || 'Espacio sin nombre',
      date: finalDateStr,
      startTime: selectedRange.startTime,
      endTime: selectedRange.endTime,
      title: selectedRange.title.trim(),
      description: selectedRange.description?.trim() || '',
      reservedBy: {
        uid: user.uid,
        displayName: user.displayName,
        email: user.email
      },
      ...(selectedRange.delegatedTo && {
        delegatedTo: selectedRange.delegatedTo
      }),
      createdAt: Timestamp.now()
    };

    try {
      const docRef = doc(collection(db, 'reservations'));
      await setDoc(docRef, reservationData);

      // Enviar emails de notificación
      await sendReservationEmails({
        ...reservationData,
        id: docRef.id
      });

      setFeedback('¡Reserva creada con éxito! Se han enviado las notificaciones por email.');
      setFeedbackType('success');
      if (onCreateReservation) {
        onCreateReservation(reservationData);
      }
      // Cerrar modal tras 1.5s
      setTimeout(() => {
        setShowModal(false);
      }, 1500);
    } catch (error) {
      console.error('Error al crear reserva:', error);
      setFeedback('Error al crear la reserva. Por favor, inténtalo de nuevo.');
      setFeedbackType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tooltip sin descripción
  const ReservationTooltip = ({ reservation }) => {
    if (!reservation) return null;
    return (
      <div
        className={styles.tooltip}
        style={{
          position: 'fixed',
          left: reservation.position.x + 10,
          top: reservation.position.y + 10
        }}
      >
        <div className={styles.tooltipHeader}>
          <span className={styles.tooltipTitle}>{reservation.title}</span>
        </div>
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipItem}>
            <Clock className={styles.tooltipIcon} size={14} />
            <span>{reservation.startTime} - {reservation.endTime}</span>
          </div>
          <div className={styles.tooltipItem}>
            <User className={styles.tooltipIcon} size={14} />
            <span>{reservation.reservedBy.displayName}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render de cada celda del calendario
  const renderDayCell = (dayIndex, slotIndex) => {
    const timeSlot = timeSlots[slotIndex];
    const reservation = getSlotReservation(dayIndex, timeSlot);
    const isSelectedRange =
      (dragStart && dragEnd && isDragging && dayIndex === dragStart.dayIndex)
        ? slotIndex >= Math.min(dragStart.slotIndex, dragEnd.slotIndex) &&
          slotIndex <= Math.max(dragStart.slotIndex, dragEnd.slotIndex)
        : false;

  // Verificamos si esta celda es parte de una reserva existente
  const isPartOfReservation = () => {
    if (!reservation) return false;
    const startSlotIndex = timeSlots.indexOf(reservation.startTime);
    const endSlotIndex = timeSlots.indexOf(reservation.endTime);
    return slotIndex >= startSlotIndex && slotIndex < endSlotIndex;
  };

  // Calculamos la altura de la reserva
  const calculateReservationHeight = (res) => {
    if (!res) return 0;
    const startSlotIndex = timeSlots.indexOf(res.startTime);
    const endSlotIndex = timeSlots.indexOf(res.endTime);
    return (endSlotIndex - startSlotIndex) * 24; // 24px es la altura de cada slot
  };

  let cellClassList = [styles.dayCell];
  
  if (isCurrentDay(weekDays[dayIndex].date)) {
    cellClassList.push(styles.currentDayCell);
  }
  
  if (isSelectedRange && !isPartOfReservation()) {
    cellClassList.push(styles.selectedCell);
  }

  // Si es parte de una reserva pero no es el slot inicial,
  // añadimos una clase especial para evitar interacciones
  if (isPartOfReservation() && timeSlots.indexOf(reservation.startTime) !== slotIndex) {
    cellClassList.push(styles.reservedSlot);
  }

  return (
    <div
      key={`${dayIndex}-${slotIndex}`}
      className={cellClassList.join(' ')}
      onMouseDown={() => {
        if (!isPartOfReservation()) {
          handleMouseDown(dayIndex, slotIndex);
        }
      }}
      onMouseEnter={(e) => {
        handleMouseEnter(dayIndex, slotIndex);
      }}
    >
      {reservation && timeSlots.indexOf(reservation.startTime) === slotIndex && (
        <div
          className={`${styles.reservedCellBase} ${
            reservation.reservedBy?.uid === user?.uid
              ? styles.userReservedCell
              : styles.otherReservedCell
          }`}
          style={{
            height: `${calculateReservationHeight(reservation)}px`,
            top: 0
          }}
          onClick={(e) => handleReservationClick(e, reservation)}
        >
          <div className={styles.reservationContent}>
            <div className={styles.reservationTitle}>
              {reservation.title}
            </div>
            <div className={styles.reservationUser}>
              <User size={12} />
              {reservation.reservedBy.displayName}
            </div>
            <div className={styles.reservationTime}>
              <Clock size={12} />
              {reservation.startTime} - {reservation.endTime}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

return (
  <div className={styles.weekCalendarContainer}>
    <div className={styles.header}>
      <div className={styles.headerContent}>
        <button className={styles.navButton} onClick={goPrevWeek}>
          <ChevronLeft size={20} />
        </button>
        <h2 className={styles.title}>{calendarTitle}</h2>
        <button className={styles.navButton} onClick={goNextWeek}>
          <ChevronRight size={20} />
        </button>
      </div>
    </div>

    <div
      className={styles.calendarTable}
      ref={tableRef}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleTableMouseLeave}
    >
      <div className={styles.calendarHeaderRow}>
        <div className={styles.timeColumnHeader}>
          <Clock className={styles.headerIcon} size={16} />
          Hora
        </div>
        {weekDays.map((day, i) => (
          <div
            key={i}
            className={`${styles.dayColumnHeader} ${
              isCurrentDay(day.date) ? styles.currentDay : ''
            }`}
          >
            <div className={styles.dayName}>{day.dayName}</div>
            <div className={styles.dayDate}>{formatDM(day.date)}</div>
          </div>
        ))}
      </div>
      <div className={styles.calendarBody}>
        {timeSlots.map((slot, slotIndex) => (
          <div key={slotIndex} className={styles.calendarRow}>
            <div className={styles.timeColumnCell}>
              {slotIndex % 6 === 0 ? slot : ''}
            </div>
            {weekDays.map((_, dayIndex) => renderDayCell(dayIndex, slotIndex))}
          </div>
        ))}
      </div>
    </div>

    {/* Tooltip si existe hoveredReservation */}
    {hoveredReservation && <ReservationTooltip reservation={hoveredReservation} />}

    {/* Modal de Nueva Reserva */}
    <NewReservationModal
      show={showModal}
      onClose={() => setShowModal(false)}
      onConfirm={handleCreateReservation}
      selectedRange={selectedRange}
      setSelectedRange={setSelectedRange}
      user={user}
      space={space}
      weekDays={weekDays}
      feedback={feedback}
      feedbackType={feedbackType}
      isSubmitting={isSubmitting}
      canDelegateReservations={canDelegateReservations}
    />

    {/* Modal de Detalles de Reserva */}
    {showDetailsModal && (
      <ReservationDetailsModal
        reservation={selectedReservation}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedReservation(null);
        }}
      />
    )}
  </div>
);
};

export default WeekCalendar;

/* ================================ */
/* ==  Funciones auxiliares     == */
/* ================================ */

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function generateWeekDays(startDate) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const dayName = d.toLocaleString('es-ES', { weekday: 'long' });
    days.push({ date: d, dayName: capitalize(dayName), dayIndex: i });
  }
  return days;
}

function generateTimeSlots(start, end, step) {
  const slots = [];
  let [hStart, mStart] = start.split(':').map(Number);
  const [hEnd, mEnd] = end.split(':').map(Number);

  while (hStart < hEnd || (hStart === hEnd && mStart <= mEnd)) {
    const hh = String(hStart).padStart(2, '0');
    const mm = String(mStart).padStart(2, '0');
    slots.push(`${hh}:${mm}`);
    mStart += step;
    if (mStart >= 60) {
      hStart++;
      mStart -= 60;
    }
  }
  return slots;
}

function formatYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDM(date) {
  return `${date.getDate()}/${date.getMonth() + 1}`;
}

function getWeekRangeTitle(weekDays) {
  if (!weekDays || !weekDays.length) return '';
  const first = weekDays[0].date;
  const last = weekDays[6].date;
  const firstMonthName = first.toLocaleString('es-ES', { month: 'long' });
  const lastMonthName = last.toLocaleString('es-ES', { month: 'long' });
  const firstYear = first.getFullYear();
  const lastYear = last.getFullYear();

  if (firstMonthName === lastMonthName && firstYear === lastYear) {
    return `${capitalize(firstMonthName)} ${firstYear}`;
  }
  return `${capitalize(firstMonthName)} ${firstYear} - ${capitalize(lastMonthName)} ${lastYear}`;
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function isCurrentDay(date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}
